import React, { useState, useEffect, useRef } from 'react';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import { Auth } from 'aws-amplify';
import CloseIcon from '@mui/icons-material/Close';
import { DialogHeader, DialogHeading } from '../Episode/Episode.Styled';
import Typography from '@mui/material/Typography';
import { commonQueryParams } from '../../services/QueryParams/QueryParams';
import { getAuthorizationListReport } from '../Report/services/reportsApi';
import { AUTHORIZATION_REPORTS_URL, dateFormat } from '../../constants';
import { useReactToPrint } from 'react-to-print';
import {
  renderExpandCell,
  renderDateCell,
  formatDateMDY,
  format180DateMDY
} from '../common/commonUtils/commonUtils';
import PopSheetPdfDataGrid from './PopSheetPdfDataGrid';
import PopSheetPdfTable from './PopSheetPdfTable';
import { PdfGrid } from '../ui/styled';
import moment from 'moment';

const AuthorizationPdfDialog = (props) => {
  const { openAuthorizationPdfDialog, setOpenAuthorizationPdfDialog } = props;

  const [user, setUser] = useState(null);
  const [authorizationList, setAuthorizationList] = useState({
    isLoading: false,
    data: [],
    total: 0,
    page: 1,
    pageSize: 10,
  });

  const componentRef = useRef();
  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
  });

  useEffect(() => {
    if (user?.signInUserSession.accessToken.jwtToken) {
      fetchData();
    }
    getUser().then((userData) => setUser(userData));
  }, [user?.signInUserSession.accessToken.jwtToken]);

  const getUser = () => {
    return Auth.currentAuthenticatedUser()
      .then((userData) => userData)
      .catch(() => console.log('Not signed in'));
  };

  const fetchData = async () => {
    setAuthorizationList((prveState) => ({ ...prveState, isLoading: true }));
    const commonParams = commonQueryParams(
      AUTHORIZATION_REPORTS_URL,
      null,
      authorizationList
    );
    const authorization = `${user?.signInUserSession.accessToken.jwtToken}`;
    const authorizationListReport = await getAuthorizationListReport({
      commonParams,
      authorization,
    });
    //console.log(authorizationListReport);
    setAuthorizationList((prveState) => ({
      ...prveState,
      ...authorizationListReport,
    }));
  };

  const groupedData = authorizationList?.data.reduce((acc, item) => {
    const groupKey = item.cntKey
      ? `${item.cntKey} Westcare ${item.orgCity}`
      : 'Case Manager Not Assigned';
    if (acc[groupKey]) {
      acc[groupKey].push(item);
    } else {
      acc[groupKey] = [item];
    }
    return acc;
  }, {});

  const sortedGroupKeys = Object.keys(groupedData).sort((a, b) => {
    if (a === 'Case Manager Not Assigned') return -1;
    if (b === 'Case Manager Not Assigned') return 1;
    return a.localeCompare(b);
  });

  const handleClose = () => {
    setOpenAuthorizationPdfDialog(false);
  };

  const columnsData = [
    {
      field: 'cboID',
      headerName: 'Cbp ID',
      minWidth: 50,
      flex: 1,
    },
    {
      field: 'cboName',
      headerName: 'CBP Name',
      minWidth: 180,
      flex: 1,
    },
    {
      field: 'cbo_ModID',
      headerName: 'Mod',
      minWidth: 60,
      flex: 1,
    },
    {
      field: 'cnyName',
      headerName: 'CBP County',
      minWidth: 100,
      flex: 1,
    },
    {
      field: 'sapath_AthTypID',
      headerName: 'Auth Type',
      renderCell: renderExpandCell,
      minWidth: 120,
      flex: 1,
    },
    {
      field: 'epdIcnCDC',
      headerName: 'CDC',
      minWidth: 80,
      flex: 1,
    },
    {
      field: 'prtKey',
      headerName: 'Participant Name',
      minWidth: 150,
      flex: 1,
    },
    // {
    //   field: 'orgCity',
    //   minWidth: 150,
    //   flex: 1,
    // },
    {
      field: 'maxPartCntDate',
      headerName: 'Last Part Contact Date',
      type: 'date',
      renderCell: renderDateCell,
      minWidth: 90,
      flex: 1,
    },
    {
      field: 'maxCBPCntDate',
      headerName: 'Last CBP Contact Date',
      type: 'date',
      renderCell: renderDateCell,
      minWidth: 90,
      flex: 1,
    },
    {
      field: 'maxAorCntDate',
      headerName: 'Last AOR Contact Date',
      type: 'date',
      renderCell: (params) => {
        return <Typography>{formatDateMDY(params.value)}</Typography>;
      },
      minWidth: 90,
      flex: 1,
    },
    {
      field: 'epdIcnDateRel',
      headerName: 'Custody Release Date',
      type: 'date',
      renderCell: renderDateCell,
      minWidth: 90,
      flex: 1,
    },
    //{
    //  field: 'sapAthID',
    //  headerName: 'Ath ID',
    //  flex: 1,
    //},
    {
      field: 'sapAthAdmDate',
      headerName: 'CBP Admit Date',
      type: 'date',
      renderCell: renderDateCell,
      minWidth: 90,
      flex: 1,
    },
    {
      field: 'sapAthOvrlEndDate',
      headerName: 'Auth Ovrl End Date',
      type: 'date',
      renderCell: renderDateCell,
      minWidth: 90,
      flex: 1,
    },
    {
     field: 'epdDchDate',
     headerName: '180 Epd Dch Date',
     type: 'date',
     renderCell: renderDateCell,
     flex: 1,
     renderCell: (params) => {
        return <Typography>{format180DateMDY(params.row.sapAthOvrlEndDate)}</Typography>;
      },
    },
  ];

  return (
    <Dialog
      fullScreen
      open={openAuthorizationPdfDialog}
      onClose={setOpenAuthorizationPdfDialog}
    >
      <DialogHeader>
        <DialogHeading variant="h4">&nbsp;</DialogHeading>
        <Box>
          <Button variant="contained" onClick={() => handlePrint()}>
            Print
          </Button>
          <IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogHeader>
      <div container ref={componentRef}>
        {sortedGroupKeys?.map((item, index) => (
          <div key={index}>
            <PopSheetPdfTable
              //key={index}
              name={item}
              cols={columnsData}
              rows={groupedData[item]}
              //componentRef={componentRef}
              //columnsData={columnsData}
              //groupedData={groupedData}
              //authorizationList={authorizationList}
            />
          <div className="page-break-after" />
          </div>
        ))}
      </div>
{/*      <PdfGrid container ref={componentRef}>
        {sortedGroupKeys?.map((item, index) => (
          <div key={index}>
            <PopSheetPdfDataGrid
              key={index}
              item={item}
              componentRef={componentRef}
              columnsData={columnsData}
              groupedData={groupedData}
              authorizationList={authorizationList}
            />
          <div className="page-break-after" />
          </div>
        ))}
      </PdfGrid>
*/}    </Dialog>
  );
};

export default AuthorizationPdfDialog;
